import { useState, useEffect, type FormEvent } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowRight, Shield, Eye, EyeOff, AlertCircle } from 'lucide-react'
import { useAuth } from '@/contexts/AuthProvider'
import AdminParagonStyleBlock from '@/components/design-system/AdminParagonStyleBlock'
import { supabase } from '@/lib/supabase'
import { isDevLoginEnabled, devSignIn, getDevSession, DEV_USERS } from '@/lib/dev-login'

const SUPERADMIN_EMAILS = ['austin@phyre.tech']

/* ============================================================
   ParagonLoginPage — Agency-branded sign-in for PARAGON staff
   Route: /paragon/login
   ============================================================ */

export default function ParagonLoginPage() {
  const { signIn, user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/paragon/admin/fleet'

  const [authState, setAuthState] = useState({
    email: '',
    password: '',
    loading: false,
    error: null as string | null,
    mfaStep: false,
    mfaCode: '',
    showPassword: false,
  })
  const { email, password, loading, error, mfaStep, mfaCode, showPassword } = authState

  // Redirect if already authenticated + has agency/platform access
  useEffect(() => {
    if (!user) return
    let cancelled = false

    const checkAccess = async () => {
      const devSession = getDevSession()
      if (devSession) { navigate(from, { replace: true }); return }
      if (user.email && SUPERADMIN_EMAILS.includes(user.email.toLowerCase())) { navigate(from, { replace: true }); return }
      const { data: roles } = await supabase.from('user_roles').select('role').eq('user_id', user.id).in('role', ['agency_admin', 'agency_designer']).limit(1)
      if (!cancelled && roles && roles.length > 0) { navigate(from, { replace: true }); return }
      const { data: platformAdmin } = await supabase.from('platform_admins').select('id').eq('user_id', user.id).maybeSingle()
      if (!cancelled && platformAdmin) navigate(from, { replace: true })
    }

    checkAccess()
    return () => { cancelled = true }
  }, [user, from, navigate])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setAuthState(prev => ({ ...prev, error: null, loading: true }))

    const trimmed = email.trim()
    if (!trimmed || !password) { setAuthState(prev => ({ ...prev, error: 'Email and password are required.', loading: false })); return }

    const { error: signInError } = await signIn(trimmed, password)
    if (signInError) { setAuthState(prev => ({ ...prev, error: signInError.message, loading: false })); return }

    const { data: session } = await supabase.auth.getUser()
    if (!session?.user) { setAuthState(prev => ({ ...prev, error: 'Authentication failed.', loading: false })); return }

    if (session.user.email && SUPERADMIN_EMAILS.includes(session.user.email.toLowerCase())) {
      setAuthState(prev => ({ ...prev, mfaStep: true, loading: false })); return
    }

    const { data: roles } = await supabase.from('user_roles').select('role').eq('user_id', session.user.id).in('role', ['agency_admin', 'agency_designer']).limit(1)
    if (!roles || roles.length === 0) {
      const { data: platformAdmin } = await supabase.from('platform_admins').select('id').eq('user_id', session.user.id).maybeSingle()
       if (!platformAdmin) { setAuthState(prev => ({ ...prev, error: 'Access denied. You are not a Paragon agency member.', loading: false })); await supabase.auth.signOut(); return }
    }

    setAuthState(prev => ({ ...prev, mfaStep: true, loading: false }))
  }

  const handleVerifyMfa = (e: FormEvent) => {
    e.preventDefault()
    // MFA verification — in production this would validate the TOTP code
    navigate(from, { replace: true })
  }

  const handleDevLogin = (userIndex: number) => {
    devSignIn(userIndex)
    window.location.assign('/paragon/admin/fleet')
  }

  return (
    <>
      <AdminParagonStyleBlock />
      <div className="w-full min-h-screen bg-black flex flex-col lg:flex-row min-h-0 overflow-y-auto lg:overflow-hidden">

        {/* ── Left brand panel ── */}
        <div className="w-full lg:w-[45%] flex flex-col justify-between p-6 sm:p-10 lg:p-12 relative overflow-hidden border-b lg:border-b-0 lg:border-r border-white/[0.04] min-h-[260px] lg:min-h-0">
          {/* Ambient glow */}
          <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] rounded-full bg-[rgba(201,168,76,0.03)] blur-[150px] pointer-events-none" />

          {/* Top: logo + labels */}
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-16">
              <div className="w-8 h-8 rounded-lg bg-[#C9A84C] flex items-center justify-center text-[12px] font-bold text-black select-none">
                P
              </div>
              <div>
                <div className="text-[14px] font-mono tracking-[0.12em] text-white/30">PARAGON</div>
                <div className="text-[11px] text-white/15 font-mono tracking-wider mt-0.5">AGENCY OPERATIONS</div>
              </div>
            </div>

            <h1 className="font-['Cormorant_Garamond',_serif] text-[38px] sm:text-[44px] lg:text-[52px] leading-[1.05] text-zinc-50 font-light mb-4">
              Fleet<br />Command
            </h1>
            <p className="text-[14px] sm:text-[15px] text-white/30 leading-[1.7] max-w-[320px]">
              Unified agency operations for managing salon fleets, migrations, and tenant health at scale.
            </p>
            <div className="w-16 h-px bg-[#C9A84C]/50 mt-8 ember-line" />
          </div>

          {/* Bottom: compliance badges */}
          <div className="relative z-10 flex items-center gap-2">
            <Shield className="w-4 h-4 text-white/15" />
            <span className="text-[11px] text-white/15">SOC2 Type II · HIPAA-Ready · Row-Level Security</span>
          </div>
        </div>

        {/* ── Right form panel ── */}
        <div className="w-full lg:w-[55%] flex items-center justify-center px-4 py-8 sm:px-6 lg:px-0 lg:py-0">
          <div className="w-full max-w-[380px] px-0 sm:px-2 lg:px-8">

            {!mfaStep ? (
              /* Step 1: email/password */
              <form onSubmit={handleSubmit} className="space-y-5" id="paragon-login-form">
                <div className="mb-8">
                  <h2 className="text-[20px] text-zinc-50 font-medium mb-1">Sign in</h2>
                  <p className="text-[13px] text-white/30">Agency credentials required. MFA enforced.</p>
                </div>

                {error && (
                  <div role="alert" className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[rgba(248,113,113,0.06)] border border-[rgba(248,113,113,0.12)]">
                    <AlertCircle className="w-3.5 h-3.5 text-[rgba(248,113,113,0.7)] flex-shrink-0" />
                    <p className="text-[12px] text-[rgba(248,113,113,0.8)]">{error}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="paragon-login-email" className="text-[11px] text-white/25 mb-1.5 block tracking-wider">EMAIL</label>
                  <input
                    id="paragon-login-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                     onChange={e => setAuthState(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="you@agency.com"
                    className="w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-white/[0.08] text-[14px] text-zinc-50 placeholder-white/15 focus:border-[rgba(201,168,76,0.3)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.3)] transition-colors duration-150"
                  />
                </div>

                <div>
                  <label htmlFor="paragon-login-password" className="text-[11px] text-white/25 mb-1.5 block tracking-wider">PASSWORD</label>
                  <div className="relative">
                    <input
                      id="paragon-login-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={password}
                       onChange={e => setAuthState(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="••••••••"
                      className="w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-white/[0.08] text-[14px] text-zinc-50 placeholder-white/15 focus:border-[rgba(201,168,76,0.3)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.3)] transition-colors duration-150 pr-10"
                    />
                    <button
                      type="button"
                       onClick={() => setAuthState(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/20 hover:text-white/40 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  id="paragon-login-submit"
                  className="w-full py-3 rounded-lg text-[14px] font-medium text-black bg-[#C9A84C] hover:bg-[#D4B85C] glow-btn flex items-center justify-center gap-2 transition-[background-color,color,border-color,opacity] duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <>Continue <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>

                <div className="mt-6 pt-4 border-t border-white/[0.04] flex items-center justify-between">
                  <button type="button" className="text-[12px] text-white/20 hover:text-white/35 transition-colors">
                    Forgot password?
                  </button>
                  <button type="button" className="text-[12px] text-[rgba(201,168,76,0.5)] hover:text-[#C9A84C] transition-colors">
                    Request access
                  </button>
                </div>
              </form>
            ) : (
              /* Step 2: MFA */
              <form onSubmit={handleVerifyMfa} className="space-y-5">
                <div className="mb-8">
                  <div className="w-12 h-12 rounded-xl bg-[rgba(99,102,241,0.1)] flex items-center justify-center mb-5">
                    <Shield className="w-6 h-6 text-[rgba(99,102,241,0.7)]" />
                  </div>
                  <h2 className="text-[20px] text-zinc-50 font-medium mb-1">Two-factor authentication</h2>
                  <p className="text-[13px] text-white/30">Enter the 6-digit code from your authenticator app.</p>
                </div>

                {error && (
                  <div role="alert" className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[rgba(248,113,113,0.06)] border border-[rgba(248,113,113,0.12)]">
                    <AlertCircle className="w-3.5 h-3.5 text-[rgba(248,113,113,0.7)] flex-shrink-0" />
                    <p className="text-[12px] text-[rgba(248,113,113,0.8)]">{error}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="paragon-login-mfa-code" className="text-[11px] text-white/25 mb-1.5 block tracking-wider">AUTHENTICATION CODE</label>
                  <input
                    id="paragon-login-mfa-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={mfaCode}
                     onChange={e => setAuthState(prev => ({ ...prev, mfaCode: e.target.value.replace(/\D/g, '') }))}
                    placeholder="000000"
                    className="w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-white/[0.08] text-[24px] font-mono text-center tracking-[0.3em] text-zinc-50 placeholder-white/15 focus:border-[rgba(201,168,76,0.3)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.3)] transition-colors duration-150"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-3 rounded-lg text-[14px] font-medium text-black bg-[#C9A84C] hover:bg-[#D4B85C] glow-btn flex items-center justify-center gap-2 transition-[background-color,color,border-color,opacity] duration-200"
                >
                  Verify <Shield className="w-4 h-4" />
                </button>

                <div className="mt-4 text-center">
                  <button
                    type="button"
                     onClick={() => setAuthState(prev => ({ ...prev, mfaStep: false }))}
                    className="text-[12px] text-white/20 hover:text-white/35 transition-colors"
                  >
                    Use a different account
                  </button>
                </div>
              </form>
            )}

            {/* Session info bar */}
            <div className="mt-8 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-[rgba(52,211,153,0.6)] flex-shrink-0" />
              <span className="text-[11px] font-mono text-white/20">TLS 1.3</span>
              <span className="text-[11px] text-white/15">Session: encrypted</span>
              <span className="text-[11px] font-mono text-white/15 ml-auto">v2.4.1</span>
            </div>

            {/* Dev Login */}
            {isDevLoginEnabled() && (
              <>
                <div className="relative my-5">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-amber-500/20" /></div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-black px-3 text-amber-400/60 font-mono">⚡ dev mode</span>
                  </div>
                </div>
                <div className="space-y-2">
                  {DEV_USERS.map((devUser, i) => (
                    <button
                      key={devUser.id}
                      type="button"
                      onClick={() => handleDevLogin(i)}
                      id={`paragon-dev-login-${devUser.role}`}
                      className="group flex w-full items-center gap-3 rounded-xl border border-amber-500/20 bg-amber-500/[0.04] px-4 py-3 text-left transition-[background-color,color,border-color,opacity] duration-200 hover:border-amber-500/40 hover:bg-amber-500/[0.08]"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/10 text-sm font-bold text-amber-400">
                        {devUser.name[0]}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-200/90">{devUser.name}</p>
                        <p className="text-xs text-amber-400/50">{devUser.email} · {devUser.role}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
